import type { FC } from "react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import type { Language } from "@ui/components/LanguageSelector";
import { LanguageSelector } from "@ui/components/LanguageSelector";
import { useBodyScrollLock } from "@ui/hooks/useBodyScrollLock";
import { useCurrentBreakpoint } from "@ui/hooks/useCurrentBreakpoint";
import { useUser } from "@ui/hooks/useUser";
import classNames from "classnames";

import { BurgerMenuIcon } from "@jti/brand-icons/burger-menu";
import { ProfileDefaultIcon } from "@jti/brand-icons/profile-default";
import { StoreLocatorIcon } from "@jti/brand-icons/store-locator";
import { UiCrossIcon } from "@jti/brand-icons/ui-cross";
import { Icon } from "@jti/core-components";
import type { LanguageSelectorVariantConfig } from "@jti/fe-common";

import type { BreadcrumbsList } from "../Breadcrumbs";
import type { LogoProps } from "../Logo";
import { Logo } from "../Logo";

import { HeaderBreadcrumbs } from "./components/HeaderBreadcrumbs/HeaderBreadcrumbs";
import type { MobileNavToolRow } from "./components/MobileNavigation";
import { MobileNavigation } from "./components/MobileNavigation";
import type { NavLinks } from "./components/Navigation";
import { Navigation } from "./components/Navigation";
import { SearchHeaderBanner } from "./components/SearchHeaderBanner/SearchHeaderBanner";
import type { ToolsProps } from "./components/Tools";
import { Tools } from "./components/Tools";
import { useBuilderHeaderStyle } from "./hooks/useBuilderHeaderStyle";
import { useHeaderScrollStore } from "./hooks/useHeaderScrollStore";
import { useOnScrollDown } from "./hooks/useOnScrollDown";

import { HeaderProvider } from "./context";

import logoStyles from "../Logo/Logo.module.scss";
import styles from "./Header.module.scss";

export type AccountDropdownSettings = {
  logoutRedirectPage?: string;
  logoutLabel?: string;
  greetingMessage?: string;
  hideGreetingMessage?: boolean;
  profileLink?: string;
  accountDashboardLabel?: string;
  accountDashboardLink?: string;
  ordersLabel?: string;
  ordersLink?: string;
  paymentsLabel?: string;
  paymentsLink?: string;
  accountInformationLabel?: string;
  accountInformationLink?: string;
  packCodesLink?: string;
  packCodesLabel?: string;
  tierTranslations?: Record<string, string>;
  hideLoyaltyTiers?: boolean;
};

export const PAGE_HEADER_ID = "page-header";

type HeaderProps = {
  isDarkMode?: boolean;
  logo: Omit<LogoProps, "isDarkMode">;
  hideNavigation?: boolean;
  isSimplified?: boolean;
  navLinks: NavLinks;
  languages: Language[];
  breadcrumbsList: BreadcrumbsList;
  renderSubmenu?: (id: string, isDarkMode?: boolean) => JSX.Element;
  accountLink?: string;
  accountLabel?: string;
  accountDropdownSettings?: AccountDropdownSettings;
  storeLocatorLink?: string;
  storeLocatorLabel?: string;
  overlayHeader?: boolean;
  onLogOut: () => void;
  withCart: boolean;
  cartLabel?: string;
  cartItemsCount?: number;
  onCartOpen?: () => void;
  useHeadingTagForBreadcrumbs?: boolean;
  isEnabledUserIndication?: boolean;
  isEnabledUserNameForIndication?: boolean;
  transparentHeaderTheme?: "dark" | "light" | "inherit";
  variant?: LanguageSelectorVariantConfig;
  hideBreadcrumbsOnMobile?: boolean;
  currentPathname?: string;
  expandBreadcrumbsLabel?: string;
  loyaltyPointsCurrency?: string;
} & Omit<ToolsProps, "isDarkMode" | "onOpenSearch" | "onCloseSearch" | "isSearchOpened" | "userDialogPortalRef">;

export const Header: FC<HeaderProps> = ({
  isDarkMode = false,
  logo,
  isSimplified = false,
  hideNavigation = false,
  navLinks,
  accountLink,
  accountLabel,
  accountLoadingLabel,
  accountDropdownSettings,
  renderMiniCart = undefined,
  mobileAppLink,
  resultsLink,
  languages,
  storeLocatorLink,
  storeLocatorLabel,
  openMenuLabel,
  closeMenuLabel,
  navAriaLabel,
  languageLabel,
  searchLabel,
  breadcrumbsList,
  renderSubmenu,
  overlayHeader = false,
  withCart = false,
  cartLabel,
  cartItemsCount,
  onCartOpen,
  useHeadingTagForBreadcrumbs,
  onLogOut,
  isEnabledUserIndication,
  isEnabledUserNameForIndication,
  currentPathname,
  transparentHeaderTheme = "inherit",
  variant = {
    mobile: "FULL_LANGUAGE_NAME",
    desktop: "SHORT_LANGUAGE_NAME",
  },
  hideBreadcrumbsOnMobile = false,
  expandBreadcrumbsLabel,
  loyaltyPointsCurrency,
}) => {
  const { isUserLoggedIn } = useUser();
  const { toggleScroll } = useBodyScrollLock(false);
  const [headerPortalHeight, setHeaderPortalHeight] = useState(0);
  const headerPortalRef = useRef<HTMLDivElement | null>(null);
  const headerPortalResizeObserverRef = useRef<ResizeObserver | null>(null);

  const containerResizeObserverRef = useRef<ResizeObserver | null>(null);

  const setContainerRef = useCallback((node: HTMLDivElement | null) => {
    if (containerResizeObserverRef.current) {
      containerResizeObserverRef.current.disconnect();
      containerResizeObserverRef.current = null;
    }
    if (node) {
      const update = () => {
        headerRef.current?.style.setProperty("--header-container-height", `${node.getBoundingClientRect().height}px`);
      };
      update();
      const resizeObserver = new ResizeObserver(update);
      resizeObserver.observe(node);
      containerResizeObserverRef.current = resizeObserver;
    }
  }, []);

  const setHeaderPortalRef = useCallback((node: HTMLDivElement | null) => {
    if (headerPortalResizeObserverRef.current) {
      headerPortalResizeObserverRef.current.disconnect();
      headerPortalResizeObserverRef.current = null;
    }
    headerPortalRef.current = node;
    if (node) {
      const resizeObserver = new ResizeObserver(() => {
        setHeaderPortalHeight(node.getBoundingClientRect().height);
      });
      resizeObserver.observe(node);
      headerPortalResizeObserverRef.current = resizeObserver;
    }
  }, []);

  const [isHydrated, setIsHydrated] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [navigationOpened, setNavigationOpened] = useState(false);
  const [isNavigationClosing, setIsNavigationClosing] = useState(false);
  const [isSearchOpened, setIsSearchOpened] = useState(false);
  const closeAnimationCleanupRef = useRef<(() => void) | null>(null);
  const headerRef = useRef<HTMLHeadElement | null>(null);
  const headerHeight = headerRef.current?.getBoundingClientRect()?.height || 0;

  const [topBannersHeight, setTopBannersHeight] = useState(0);
  const { mainBreakpoint } = useCurrentBreakpoint();
  const isMobile = ["xs", "sm"].includes(mainBreakpoint);
  const hideBreadcrumbs = hideBreadcrumbsOnMobile && isMobile;

  useEffect(() => {
    if (document.readyState === "complete") {
      setIsHydrated(true);
    } else {
      const handleLoad = () => setIsHydrated(true);
      window.addEventListener("load", handleLoad);
      return () => window.removeEventListener("load", handleLoad);
    }
  }, []);

  useEffect(() => {
    const readTopBannersHeight = () => {
      const root = document.documentElement;
      const rawHeight = getComputedStyle(root).getPropertyValue("--top-banners-height");
      const height = parseInt(rawHeight.trim(), 10);
      setTopBannersHeight(height || 0);
    };

    readTopBannersHeight();

    const observer = new MutationObserver((mutations) => {
      for (const { type, attributeName } of mutations) {
        if (type === "attributes" && attributeName === "style") {
          readTopBannersHeight();
          return;
        }
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["style"],
      subtree: false,
    });

    return () => observer.disconnect();
  }, []);

  const scrollState = useHeaderScrollStore({
    headerHeight,
    topBannersHeight,
    headerPortalHeight,
    overlayHeader,
  });

  const { isAtTop, isScrolled, isVisible, isHidden, isTransitionDisabled, isVisibleOverlay } = scrollState;

  const isBuilderStyleSkipped = useBuilderHeaderStyle({
    headerPortalHeight,
    isVisible: (isVisible || isAtTop) && !overlayHeader,
    isVisibleOverlay,
    isTransitionDisabled,
    isOverlayHeader: overlayHeader,
    isHidden,
    styles,
  });

  const shouldAddFixedStyle = isBuilderStyleSkipped && overlayHeader;

  useOnScrollDown(isSearchOpened, () => setIsSearchOpened(false));

  useEffect(() => {
    if (!isNaN(headerHeight)) {
      document.documentElement.style.setProperty("--header-portal-height", `-${headerHeight - headerPortalHeight}px`);
    }
  }, [headerHeight, headerPortalHeight]);

  useEffect(() => {
    const updateViewportDistance = () => {
      if (headerRef.current) {
        const top = Math.round(headerRef.current.getBoundingClientRect().top);
        headerRef.current.style.setProperty("--header-viewport-distance", `${top}px`);
      }
    };

    updateViewportDistance();
    window.addEventListener("scroll", updateViewportDistance, { passive: true });

    return () => {
      window.removeEventListener("scroll", updateViewportDistance);
    };
  }, [topBannersHeight, navigationOpened]);

  // This useLayoutEffect sets scroll-padding-top CSS property accounting for sticky header when Next.js
  // scrolls page content into view right after navigation. Implements officially proposed solution:
  // https://nextjs.org/docs/pages/api-reference/components/link#scroll-offset-with-sticky-headers
  // useLayoutEffect() was used because useEffect() would apply new scroll-padding-top value after
  // Next.js runs its <Link> scroll behavior, resulting in scrolling to incorrect point when height
  // of the header on page where user navigated from differs from header on page user navigated to.
  useLayoutEffect(() => {
    const currentHeaderHeight = headerRef.current?.getBoundingClientRect()?.height || 0;
    document.documentElement.style.setProperty("scroll-padding-top", `${currentHeaderHeight - headerPortalHeight}px`);
  }, [headerHeight, headerPortalHeight]);

  useEffect(() => {
    toggleScroll(navigationOpened);
  }, [navigationOpened, toggleScroll]);

  useEffect(
    () => () => {
      closeAnimationCleanupRef.current?.();
      closeAnimationCleanupRef.current = null;
    },
    []
  );

  const startCloseAnimationLock = useCallback(() => {
    closeAnimationCleanupRef.current?.();
    closeAnimationCleanupRef.current = null;
    setIsNavigationClosing(true);

    const mobileNavigation = document.getElementById("mobile-navigation");
    if (!mobileNavigation) {
      setIsNavigationClosing(false);
      return;
    }

    const { transitionDuration } = window.getComputedStyle(mobileNavigation);
    const hasTransition = transitionDuration.split(",").some((durationValue) => parseFloat(durationValue.trim()) > 0);

    // Reduced motion has no transition, so release lock immediately.
    if (!hasTransition) {
      setIsNavigationClosing(false);
      return;
    }

    const onTransitionEnd = (event: TransitionEvent) => {
      if (event.target !== mobileNavigation || event.propertyName !== "max-height") {
        return;
      }

      setIsNavigationClosing(false);
      mobileNavigation.removeEventListener("transitionend", onTransitionEnd);
      closeAnimationCleanupRef.current = null;
    };

    mobileNavigation.addEventListener("transitionend", onTransitionEnd);
    closeAnimationCleanupRef.current = () => {
      mobileNavigation.removeEventListener("transitionend", onTransitionEnd);
    };
  }, []);

  const handleHamburgerClick = () => {
    if (isNavigationClosing) {
      return;
    }

    if (navigationOpened) {
      startCloseAnimationLock();
      setNavigationOpened(false);
      return;
    }

    setNavigationOpened(true);
  };

  const closeNavigation = useCallback(() => {
    if (!navigationOpened) {
      return;
    }

    startCloseAnimationLock();
    setNavigationOpened(false);
  }, [navigationOpened, startCloseAnimationLock]);

  const mobileToolsLinks: MobileNavToolRow[] = [
    ...(accountLink && !isUserLoggedIn
      ? [{ id: "account" as const, label: accountLabel ?? "", href: accountLink, icon: ProfileDefaultIcon }]
      : []),
    ...(storeLocatorLink
      ? [
          {
            id: "store-locator" as const,
            label: storeLocatorLabel ?? "",
            href: storeLocatorLink,
            icon: StoreLocatorIcon,
          },
        ]
      : []),
  ];

  const openSearch = () => {
    setIsSearchOpened(true);
  };

  const closeSearch = () => {
    setIsSearchOpened(false);
  };

  const hasBackground = isSearchOpened || navigationOpened || (isScrolled && overlayHeader);
  const isTransparent = isHydrated && isAtTop && overlayHeader && !isHovered;

  function getTheme(): "light" | "dark" {
    if (!hasBackground && isTransparent && transparentHeaderTheme !== "inherit") {
      return transparentHeaderTheme;
    }
    return isDarkMode ? "dark" : "light";
  }

  const theme = getTheme();
  return (
    <HeaderProvider>
      <header
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={classNames(styles.headerPosition, styles.header, {
          "dark-theme": theme === "dark",
          "light-theme": isTransparent && theme === "light",
          [styles.hover]: isHovered,
          [styles.transparent]: isHydrated && isAtTop && overlayHeader,
          [styles.withBackground]: hasBackground,
          [styles.hidden]: isHidden,
          [styles.visible]: (isVisible || isAtTop) && !overlayHeader,
          [styles.transitionDisabled]: isTransitionDisabled,
          [styles.visibleAsOverlay]: isVisibleOverlay,
          [styles.fixed]: shouldAddFixedStyle,
        })}
        id={PAGE_HEADER_ID}
        data-testid="header"
        ref={headerRef}
      >
        <div className={styles.container} ref={setContainerRef}>
          <div className={styles.desktopLogo}>
            <Logo isDarkMode={true} {...logo} className={classNames({ [logoStyles.hidden]: theme !== "dark" })} />
            <Logo isDarkMode={false} {...logo} className={classNames({ [logoStyles.hidden]: theme === "dark" })} />
          </div>
          <div className={styles.content}>
            <div className={styles.mobileLogo}>
              <Logo isDarkMode={true} {...logo} className={classNames({ [logoStyles.hidden]: theme !== "dark" })} />
              <Logo isDarkMode={false} {...logo} className={classNames({ [logoStyles.hidden]: theme === "dark" })} />
            </div>
            {!hideNavigation && !isSimplified && (
              <>
                <div className={styles.navigation}>
                  <div className={styles.navigationContent}>
                    <Navigation navLinks={navLinks} renderSubmenu={renderSubmenu} storeLocatorLink={storeLocatorLink} />
                  </div>
                </div>
                <MobileNavigation
                  isOpen={navigationOpened}
                  onClose={closeNavigation}
                  navLinks={navLinks}
                  toolsLinks={mobileToolsLinks}
                  languages={languages}
                  isUserLoggedIn={isUserLoggedIn}
                  logoutLabel={accountDropdownSettings?.logoutLabel ?? ""}
                  onLogOut={onLogOut}
                  navLabel={navAriaLabel ?? ""}
                  closeLabel={closeMenuLabel ?? ""}
                  languageLabel={languageLabel ?? ""}
                  searchLabel={searchLabel}
                  isSearchOpened={isSearchOpened}
                  renderSubmenu={renderSubmenu}
                  onOpenSearch={openSearch}
                  onCloseSearch={closeSearch}
                  cartLabel={cartLabel}
                  cartItemsCount={cartItemsCount}
                  onCartOpen={onCartOpen}
                  accountDropdownSettings={accountDropdownSettings}
                  loyaltyPointsCurrency={loyaltyPointsCurrency}
                />
              </>
            )}
            {!isSimplified && (
              <div className={styles.toolsContainer}>
                <div
                  className={classNames(styles.tools, {
                    [styles.toolsHiddenMobile]: navigationOpened,
                  })}
                  data-testid="header-tools"
                >
                  <Tools
                    accountLink={accountLink}
                    accountLabel={accountLabel}
                    accountLoadingLabel={accountLoadingLabel}
                    accountDropdownSettings={accountDropdownSettings}
                    renderMiniCart={renderMiniCart}
                    mobileAppLink={mobileAppLink}
                    storeLocatorLink={storeLocatorLink}
                    storeLocatorLabel={storeLocatorLabel}
                    resultsLink={resultsLink}
                    onCloseSearch={closeSearch}
                    onOpenSearch={openSearch}
                    isSearchOpened={isSearchOpened}
                    currentPathname={currentPathname}
                    withCart={withCart}
                    onLogOut={onLogOut}
                    isEnabledUserIndication={isEnabledUserIndication}
                    isEnabledUserNameForIndication={isEnabledUserNameForIndication}
                    userDialogPortalRef={headerRef}
                  />
                  <div className={styles.desktopLanguageSelector} data-testid="desktopLanguageSelector">
                    <LanguageSelector
                      languages={languages}
                      popupContext="NAVIGATION"
                      variant={variant.desktop ?? "SHORT_LANGUAGE_NAME"}
                    />
                  </div>
                </div>
                <button
                  className={classNames(styles.burgerMenu, {
                    [styles.burgerMenuDisabled]: isNavigationClosing,
                  })}
                  onClick={handleHamburgerClick}
                  disabled={isNavigationClosing}
                  aria-expanded={navigationOpened}
                  aria-controls="mobile-navigation"
                  aria-label={navigationOpened ? closeMenuLabel : openMenuLabel}
                >
                  <Icon icon={navigationOpened ? UiCrossIcon : BurgerMenuIcon} size="sm" />
                </button>
              </div>
            )}
          </div>
        </div>
        <div className={styles.searchLayerContainer}>
          <SearchHeaderBanner isOpened={isSearchOpened} onClose={closeSearch} resultsPageUrl={resultsLink ?? ""} />
        </div>
        <div className={classNames({ [styles.hideBreadcrumbs]: hideBreadcrumbs })}>
          <HeaderBreadcrumbs
            breadcrumbsList={breadcrumbsList}
            useHeadingTagForBreadcrumbs={useHeadingTagForBreadcrumbs}
            expandBreadcrumbsLabel={expandBreadcrumbsLabel}
          />
        </div>
        <div
          className={classNames({
            [styles.headerPortalHidden]: navigationOpened,
          })}
          ref={setHeaderPortalRef}
          data-portal="header"
        ></div>
      </header>
    </HeaderProvider>
  );
};
